import axios, { AxiosInstance } from 'axios'
import { message } from 'antd'
import { signOut } from '@/utils/utils'
import i18next from "i18next";
import { store } from './index'
import urljoin from 'url-join'
import { history } from "@/route";


import logger from '@/utils/logger';
import { getItem } from '@/utils/secureStorage';

export const picURL = "https://wpg.wapipay.com/remit-service/profile/" //image url

// Checkout result pages base URL — same pattern as invitation link (hardcoded, not env-based).
// Used when building checkout success/fail URLs for mobile app redirects.
export const checkoutBaseUrl = "https://global.wapipay.com"

export const productionUrl = "app-test.wapipay.com" //prod environment
export const stagingUrl = "https://dev.wapipay.com/"    //staging environment
// export const devUrl = "https://app.wapipay.com/"  //dev environment
export const devUrl = "https://wapi-devs.wapipay.com/"   //dev environment


export const productionWebUrl = "app-test.wapipay.com/" // prod environment
export const stagingWebUrl = "https://dev.wapipay.com/"    // staging environment
// export const devWebUrl = "https://app.wapipay.com/"   //dev environment
export const devWebUrl = "https://wapi-devs.wapipay.com/"   //dev environment

// Get environment from REACT_APP__ENV or fallback to NODE_ENV
const getEnvironment = () => {
  return process.env.REACT_APP__ENV || process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development';
};

const environment = getEnvironment();

// In development, use empty baseURL so requests go through the dev server proxy (avoids CORS)
export const baseURL =
  environment === 'production' ? productionUrl :
    environment === 'staging' ? stagingUrl :
      environment === 'development' ? '' : devUrl; // dev: use proxy; fallback: direct to dev server

export const baseWebURL =
  environment === 'production' ? productionWebUrl :
    environment === 'staging' ? stagingWebUrl :
      devWebUrl; // default to dev for development and any other case

/** Build checkout success URL (for mobile app / redirects). Base defined above (checkoutBaseUrl). */
export const getCheckoutSuccessUrl = (params?: { amount?: string; date?: string; referenceId?: string }): string => {
  const base = `${checkoutBaseUrl}/#/checkoutSuccess`
  if (!params || Object.keys(params).length === 0) return base
  const search = new URLSearchParams()
  if (params.amount) search.set('amount', params.amount)
  if (params.date) search.set('date', params.date)
  if (params.referenceId) search.set('referenceId', params.referenceId)
  return `${base}?${search.toString()}`
}

/** Build checkout failed URL (for mobile app / redirects). Base defined above (checkoutBaseUrl). */
export const getCheckoutFailedUrl = (params?: { reason?: string; retryUrl?: string }): string => {
  const base = `${checkoutBaseUrl}/#/checkoutFailed`
  if (!params || Object.keys(params).length === 0) return base
  const search = new URLSearchParams()
  if (params.reason) search.set('reason', params.reason)
  if (params.retryUrl) search.set('retryUrl', params.retryUrl)
  return `${base}?${search.toString()}`
}

export const getToken = () => {
  const tokenItem = document.cookie.split(';').find(item => ~item.substring(0, 10).indexOf('token'))
  const token = tokenItem && tokenItem.slice(tokenItem.indexOf('=') + 1)
  if (token) {
    return token
  }
  return ''
}

let params = {
  baseURL,
  timeout: 100000,
  headers: {
  }
}

let instance: AxiosInstance = axios.create(params)

instance.interceptors.request.use(function (config) {
  const {
    // @ts-ignore
    tmpToken,
    url
  } = config
  const token = getToken()
  try {
    const state = store.getState()?.UserInfoReducer
    const id = state?.userInfo?.id
    if (id) config.headers['userId'] = `${id}`
  } catch (_e) { /* store may not be ready on public pages */ }
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`
  } else if (tmpToken) {
    config.headers['tmp-token'] = tmpToken
  }
  config.headers.Version = '2.0.0'
  const lang = getItem("wapipay-website-locale") || 'zh'
  config.url = urljoin(url || '', `?lang=${lang}`)
  logger.apiRequest(config);
  return config;
}, function (error) {
  message.error(i18next.t("网络异常！"))
  return Promise.reject(error);
})


instance.interceptors.response.use(function (response) {
  const data = response?.data;
  logger.apiResponse(response);
  // if (response.config.$sourceData) {
  //   return data
  // }
  // console.log('response=>', response)
  try {
    if (response.config.responseType === 'blob') {
      return data;
    }
    // Catalog APIs may return { States: [...] } or { Cities: [...] } or { MasterPayerResults: [...] } without code wrapper
    if (data?.States || data?.Cities || data?.MasterPayerResults || data?.states || data?.cities || data?.masterPayerResults || data?.banks || data?.branches) {
      return data;
    }
    // Catalog data may be nested in { code: 200, data: { States: [...] } } or { result: { States: [...] } }
    const inner = data?.data ?? data?.result;
    if (inner && (inner?.States || inner?.states || inner?.Cities || inner?.cities || inner?.MasterPayerResults || inner?.masterPayerResults || inner?.banks || inner?.branches)) {
      return inner;
    }
    switch (data?.code) {
      case 200:
      case 0:
        return data && data.data !== undefined ? data.data : data;
      case 401:
        if (getToken()) {
          message.warning(i18next.t('Session timed out! Please log in again to continue.'))
          signOut()
          setTimeout(() => {
            history.push('/home')
            window.location.reload()
          }, 2000);
        }
        return Promise.reject(data);
      default:
        message.warning(data.msg)
        return Promise.reject(data);
    }
  } catch (error) {
    return Promise.reject(error);
  }
}, function (error) {
  const errorRes = error?.response
  const errorInfo = errorRes?.data
  logger.apiError(error);
  try {
    // Only sign out on 401 - don't sign out for requests that don't have a token (e.g. public pages)
    const isUnauthorized = errorRes?.status === 401
    if (isUnauthorized && getToken()) {
      message.warning(i18next.t('Session timed out! Please log in again to continue.'))
      signOut()
      setTimeout(() => {
        history.push('/home')
        window.location.reload()
      }, 2000);
    } else if (errorInfo?.msg) {
      message.error(errorInfo.msg)
    } else if (!isUnauthorized && !(error?.config as any)?.skipGlobalError) {
      message.error(i18next.t('Network abnormality! Please try again later!'))
    }
  } catch (error) {
    message.error(i18next.t('Network abnormality! Please try again later!'))
  }
  return Promise.reject(errorInfo);
})

export default instance
